import { useQuery } from '@tanstack/react-query';
import { useState, useCallback, useMemo, memo, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';

import type { ApiResponse } from '@/utils/deepmerge';

import { GetInitialDataForHandRequestApi } from './api/HandRequest.api';
import { IRequestType } from './api/HandRequest.interface';
import type {
  ISuppliars,
  ISuppliarWithDebt,
  IHandRequestInitialData,
} from './api/HandRequest.interface';
import LoadingStage from './components/LoadingStage';
import StoreRequest from './components/StoreRequest';
import { handleSupplierSelection } from './utils/supplierHandlers';

// Memoized store request component to prevent unnecessary renders
const MemoizedStoreRequest = memo(StoreRequest);

// Define the props interface for RequestContent
interface RequestContentProps {
  mainData: ApiResponse<IHandRequestInitialData> | undefined;
  selectedSuppliar: ISuppliarWithDebt | null;
  requestType: IRequestType;
  handleReset: () => void;
  handleSelectedSuppliar: (value: ISuppliars | null) => Promise<void>;
}

// Main page content component to separate loading logic
const RequestContent = memo(
  ({
    mainData,
    selectedSuppliar,
    requestType,
    handleReset,
    handleSelectedSuppliar,
  }: RequestContentProps) => {
    // Extract suppliers and items from mainData to avoid deep nesting in render
    const suppliers = useMemo(
      () => mainData?.data?.suppliars || [],
      [mainData?.data?.suppliars],
    );

    const items = useMemo(
      () => mainData?.data?.items || [],
      [mainData?.data?.items],
    );

    return (
      <div className="p-2">
        {requestType === IRequestType.Stores && (
          <MemoizedStoreRequest
            items={items}
            suppliar={selectedSuppliar}
            requestType={requestType}
            handleReset={handleReset}
            handleSelectedSuppliar={handleSelectedSuppliar}
            suppliers={suppliers}
          />
        )}
      </div>
    );
  },
);

RequestContent.displayName = 'RequestContent';

function RequestPerHandPage() {
  // Optimize React Query with advanced caching strategy
  const { data: mainData, isLoading: isMainDataLoading } = useQuery({
    queryKey: ['MainData'],
    queryFn: () => GetInitialDataForHandRequestApi(),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const [selectedSuppliar, setSelectedSuppliar] =
    useState<ISuppliarWithDebt | null>(null);
  const [requestType, setRequestType] = useState<IRequestType>(
    IRequestType.Stores,
  );
  const [stage] = useState(1); // Since we only use stage=1, no need for setStage

  const [loadingStage, setLoadingStage] = useState<{
    isLoading: boolean;
    type: 'upload' | 'update' | 'main';
  }>({
    isLoading: false,
    type: 'main',
  });

  // Memoize handler functions to prevent unnecessary re-renders
  const handleLoadingStage = useCallback(
    (value: boolean, type: 'upload' | 'update' | 'main') => {
      setLoadingStage({
        isLoading: value,
        type,
      });
    },
    [],
  );

  const handleSelectedSuppliar = useCallback(
    async (value: ISuppliars | null) => {
      // Skip processing if trying to set the same supplier
      if (value?.ID === selectedSuppliar?.ID) return;

      const updatedSupplier = await handleSupplierSelection({
        value,
        onLoadingChange: handleLoadingStage,
      });
      setSelectedSuppliar(updatedSupplier);
    },
    [handleLoadingStage, selectedSuppliar?.ID],
  );

  const handleReset = useCallback(() => {
    setSelectedSuppliar(null);
    setRequestType(IRequestType.Stores);
  }, []);

  // Show loading state while main data is loading
  const isLoading = isMainDataLoading || loadingStage.isLoading;
  const loadingType = isMainDataLoading ? 'main' : loadingStage.type;

  return (
    <>
      <Helmet>
        <title>طلبية شراء</title>
      </Helmet>

      {isLoading && <LoadingStage LoadingType={loadingType} />}

      {!isMainDataLoading && stage === 1 && (
        <Suspense fallback={<LoadingStage LoadingType="main" />}>
          <RequestContent
            mainData={mainData}
            selectedSuppliar={selectedSuppliar}
            requestType={requestType}
            handleReset={handleReset}
            handleSelectedSuppliar={handleSelectedSuppliar}
          />
        </Suspense>
      )}
    </>
  );
}

export default memo(RequestPerHandPage);
